import { apiFetch, readJson, readJsonArray, readErrorMessage } from './client';

export type StorageMountKind = 'Smb' | 'S3';
export type StorageAccessMode = 'Read' | 'ReadWrite';

export interface StorageMountAccessDto {
  id: string;
  role: string;
  groupObjectId: string | null;
  groupDisplayName: string | null;
  mode: StorageAccessMode;
}

export interface StorageMountDto {
  id: string;
  displayName: string;
  entraTenantId: string | null;
  kind: StorageMountKind;
  isActive: boolean;
  mentionPeerMountId: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  access: StorageMountAccessDto[];
}

export interface StorageMountSettingsViewDto {
  uncPath: string | null;
  domain: string | null;
  username: string | null;
  passwordConfigured: boolean;
  serviceUrl: string | null;
  region: string | null;
  bucketName: string | null;
  forcePathStyle: boolean | null;
  accessKeyId: string | null;
  secretAccessKeyConfigured: boolean;
  keyPrefix: string | null;
}

export interface StorageMountDetailDto extends StorageMountDto {
  settings: StorageMountSettingsViewDto;
}

export interface SmbMountSettings {
  uncPath: string;
  domain: string;
  username: string;
  password?: string; // plaintext on create/update only; never returned by the API
}

export interface S3MountSettings {
  serviceUrl?: string;
  region?: string;
  bucketName: string;
  forcePathStyle?: boolean;
  accessKeyId: string;
  secretAccessKey?: string; // plaintext on create/update only
  keyPrefix?: string;
}

export interface UpsertAccessRequest {
  role: string;
  groupObjectId?: string | null;
  groupDisplayName?: string | null;
  mode: StorageAccessMode;
}

export interface CreateMountRequest {
  displayName: string;
  kind: StorageMountKind;
  settingsJson: string;
  mentionPeerMountId?: string | null;
  access?: UpsertAccessRequest[];
}

export interface UpdateMountRequest {
  displayName?: string;
  settingsJson?: string;
  isActive?: boolean;
  mentionPeerMountId?: string | null;
  syncMentionPeerMount?: boolean;
  access?: UpsertAccessRequest[];
}

export interface DirectoryPrincipal {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string | null;
}

export interface ConnectionTestResult {
  success: boolean;
  itemCount?: number;
  error?: string;
}

export interface TestMountConnectionRequest {
  kind: StorageMountKind;
  settingsJson: string;
  existingMountId?: string | null;
}

function qs(params: Record<string, string | undefined>): string {
  const parts = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (parts.length === 0) return '';
  return `?${parts.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`).join('&')}`;
}

export async function listMounts(): Promise<StorageMountDto[]> {
  const res = await apiFetch('/api/admin/storage-mounts');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<StorageMountDto>(res);
}

export async function getMount(id: string): Promise<StorageMountDetailDto> {
  const res = await apiFetch(`/api/admin/storage-mounts/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDetailDto>(res);
}

export async function createMount(req: CreateMountRequest): Promise<StorageMountDto> {
  const res = await apiFetch('/api/admin/storage-mounts', {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDto>(res);
}

export async function updateMount(id: string, req: UpdateMountRequest): Promise<StorageMountDto> {
  const res = await apiFetch(`/api/admin/storage-mounts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDto>(res);
}

export async function deleteMount(id: string): Promise<void> {
  const res = await apiFetch(`/api/admin/storage-mounts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}

export async function testMount(id: string): Promise<ConnectionTestResult> {
  const res = await apiFetch(`/api/admin/storage-mounts/${encodeURIComponent(id)}/test`, { method: 'POST' });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ConnectionTestResult>(res);
}

export async function testMountConnection(req: TestMountConnectionRequest): Promise<ConnectionTestResult> {
  const res = await apiFetch('/api/admin/storage-mounts/test-connection', {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ConnectionTestResult>(res);
}

export async function listRoles(): Promise<string[]> {
  const res = await apiFetch('/api/admin/storage-mounts/roles');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<string>(res);
}

export async function searchGroups(q: string): Promise<DirectoryPrincipal[]> {
  const res = await apiFetch(`/api/admin/storage-mounts/groups/search${qs({ q })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<DirectoryPrincipal>(res);
}

export interface MentionPeerCandidate {
  id: string;
  displayName: string;
  entraTenantId: string | null;
  groupCount: number;
}

export interface MentionScopeGroup {
  groupObjectId: string;
  groupDisplayName: string | null;
}

export interface MentionScopePreview {
  mentionsEnabled: boolean;
  entraTenantId: string | null;
  peerMountId: string | null;
  peerMountDisplayName: string | null;
  ownGroupCount: number;
  groups: MentionScopeGroup[];
}

export async function listPeerCandidates(tenantId?: string): Promise<MentionPeerCandidate[]> {
  const res = await apiFetch(`/api/admin/storage-mounts/mention-peer-candidates${qs({ tenantId })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<MentionPeerCandidate>(res);
}

export async function setMentionPeer(
  mountId: string,
  peerMountId: string | null,
  linkBack: boolean
): Promise<void> {
  const res = await apiFetch(`/api/admin/storage-mounts/${encodeURIComponent(mountId)}/mention-peer`, {
    method: 'PUT',
    body: JSON.stringify({ peerMountId, linkBack })
  });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}

export async function getMentionScope(mountId: string, path?: string): Promise<MentionScopePreview> {
  const res = await apiFetch(
    `/api/admin/storage-mounts/${encodeURIComponent(mountId)}/mention-scope${qs({ path })}`
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<MentionScopePreview>(res);
}

